import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/permissions/privilegesService.js
import { authService } from '@/services/auth';
import { caItemsCache } from '@/services/app/store/modules/ca-items-cache';

/**
 * PrivilegesService
 * 
 * Manages project privileges with sophisticated caching and request handling:
 * - Fetches and caches privileges data from the API
 * - Provides consistent interface for accessing privileges
 * - Implements intelligent cache invalidation and refreshing
 * - Handles request deduplication to prevent redundant API calls
 * - Implements fallback mechanisms for offline and error scenarios
 * - Supports sessionStorage caching for page reloads
 */
class PrivilegesService {
  /**
   * Initialize the privileges service
   */
  constructor() {
    this.CACHE_FRESHNESS_THRESHOLD = 5 * 60 * 1000; // 5 minutes
    this.FORCE_REFRESH_COOLDOWN = 5 * 1000; // 5 seconds — prevents back-to-back forced fetches
    this.lastPrivilegesSync = null;
    this.projectPrivileges = null;
    this.needsRepopulationFlag = false;
    this.modulesNeedingRepopulation = [];
    this.pendingRequests = {};
    this.CACHE_KEY = 'projectPrivileges';
    // Bump on any deploy that changes privilege loading; restored caches with a
    // different version are discarded. Boots stale cache that survived Edge's
    // tab-restore / "continue where you left off" across browser restarts.
    this.CACHE_VERSION = 1;
    // True only after a successful fetch in *this* page-load session. A cached
    // value loaded from sessionStorage by restoreFromCache leaves this false,
    // signalling consumers (usePermissions) to re-fetch before trusting the
    // cached privilege for routing decisions.
    this.inMemoryFetchedThisSession = false;
    // Subscribers receive every update to projectPrivileges (cache restore +
    // fetch results). Lets React hooks re-render when a background refresh
    // arrives after the initial render.
    this._subscribers = new Set();

    // Mapping from Snowflake module names to frontend module names
    this.MODULE_REPOPULATION_MAP = {
      'SupplyChainManagement': ['supplychainSuppliers'],
      'SelfAssessment': ['businessSegments', 'companyQuestions'],
      'CarbonAccounting': ['emissionResults', 'activityData', 'spendVolume', 'spendDistribution', 'supplierInput'],
      'CustomerRequests': ['customerRequests']
    };

    this.defaultPrivileges = {
      'AppMode': 'OPEN',
      'CarbonAccounting': 'GRANTED_FREE'
    };

    // Privilege values for Carbon Accounting
    this.CA_PRIVILEGES = {
      FREE: 'GRANTED_FREE',
      ITEMS: 'GRANTED_ITEMS'
    };

    // Try to restore from cache on initialization
    this.restoreFromCache();
  }

  /**
   * Restore privileges from sessionStorage cache if available.
   * Validates that cached privileges belong to the CURRENT projectId — prevents
   * stale privileges from a prior tenant leaking into a new session.
   * @private
   */
  restoreFromCache() {
    try {
      if (typeof sessionStorage === 'undefined') return;

      const cachedData = sessionStorage.getItem(this.CACHE_KEY);
      if (!cachedData) return;

      const { privileges, timestamp, projectId, version, needsRepopulationFlag, modulesNeedingRepopulation } = JSON.parse(cachedData);

      // Reject cache from a different deploy. Boots stale GRANTED_FREE that
      // survived Edge's tab-restore from before a privilege-loading hotfix.
      if (version !== this.CACHE_VERSION) {
        sessionStorage.removeItem(this.CACHE_KEY);
        return;
      }

      // Reject cache entries that don't carry a projectId — they're from before this fix
      if (!projectId) {
        sessionStorage.removeItem(this.CACHE_KEY);
        return;
      }

      // Reject cache if the current projectId differs from the one the cache was written for
      const currentProjectId = this.getProjectId();
      if (currentProjectId && String(currentProjectId) !== String(projectId)) {
        console.warn('[Privileges] Cached privileges belong to a different project — discarding', {
          cachedProjectId: projectId,
          currentProjectId
        });
        sessionStorage.removeItem(this.CACHE_KEY);
        return;
      }

      if (privileges && timestamp && (Date.now() - timestamp < this.CACHE_FRESHNESS_THRESHOLD)) {
        this.projectPrivileges = privileges;
        this.lastPrivilegesSync = timestamp;
        this.needsRepopulationFlag = needsRepopulationFlag;
        this.modulesNeedingRepopulation = modulesNeedingRepopulation;
        // Note: inMemoryFetchedThisSession stays false — the cached value
        // hasn't been verified against the source yet this session.
      }
    } catch (error) {
      // Silent error in production
    }
  }

  /**
   * Save privileges to sessionStorage cache
   * @private
   */
  saveToCache() {
    try {
      if (typeof sessionStorage === 'undefined' || !this.projectPrivileges) return;

      const cacheData = {
        version: this.CACHE_VERSION,
        privileges: this.projectPrivileges,
        projectId: this.getProjectId(),
        needsRepopulationFlag: this.needsRepopulationFlag,
        modulesNeedingRepopulation: this.modulesNeedingRepopulation,
        timestamp: this.lastPrivilegesSync || Date.now()
      };

      sessionStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      // Silent error in production
    }
  }

  /**
   * Subscribe to privilege updates. Receives the new privileges object every
   * time projectPrivileges changes (fetch result, force refresh). Returns an
   * unsubscribe function. Used by usePermissions so React state catches up
   * when a background refresh completes after the initial render.
   *
   * @param {(privileges: Object) => void} callback
   * @returns {() => void} unsubscribe function
   */
  subscribe(callback) {
    if (typeof callback !== 'function') return () => {};
    this._subscribers.add(callback);
    return () => this._subscribers.delete(callback);
  }

  /**
   * @private
   */
  _notifySubscribers() {
    for (const cb of this._subscribers) {
      try {
        cb(this.projectPrivileges);
      } catch (e) {
        // A subscriber error must not break the chain.
      }
    }
  }

  /**
   * Get project ID from auth service
   * @private
   * @returns {string|null} Project ID
   */
  getProjectId() {
    return authService.getProjectId();
  }

  /**
   * Get privileges for the current project
   * Fetches privileges from API or returns from cache if fresh
   * 
   * @param {boolean} forceRefresh - Force refresh regardless of cache
   * @returns {Promise<Object>} Privileges object
   */
  async getPrivileges(forceRefresh = false) {
    // COOLDOWN: If we fetched from the API very recently, return cached data
    // even for forced calls. Multiple init paths (store, permissions, SCM, hooks)
    // all fire getPrivileges(true) within seconds of each other on page reload.
    // Privileges can't change in 5 seconds — one real fetch is enough.
    if (forceRefresh && this.projectPrivileges && this.lastPrivilegesSync &&
        Date.now() - this.lastPrivilegesSync < this.FORCE_REFRESH_COOLDOWN) {
      return this.projectPrivileges;
    }

    if(this.pendingRequest && !forceRefresh) {
      return this.pendingRequest;
    }
    try {
      // CRITICAL: For route navigation, return cached data immediately
      const isRouteNavigation = typeof window !== 'undefined' &&
        this.projectPrivileges && // We have cached data
        !forceRefresh &&
        document.visibilityState === 'visible'; // Page is visible

      if (isRouteNavigation) {
        // Check cache freshness but don't block
        const isFresh = this.lastPrivilegesSync &&
          Date.now() - this.lastPrivilegesSync < this.CACHE_FRESHNESS_THRESHOLD;

        if (isFresh) {
          // Cache is fresh, just return it
          return this.projectPrivileges;
        }

        // Cache is stale but return it anyway for immediate render
        // Stale cache logging silenced

        // Schedule background refresh
        setTimeout(() => {
          this.getPrivileges(true).catch(err => {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[PrivilegesService] Background refresh failed:', err);
            }
          });
        }, 100);

        // Return cached data immediately
        return this.projectPrivileges;
      }

      // Normal flow for initial load or forced refresh
      if (!forceRefresh && this.lastPrivilegesSync &&
        Date.now() - this.lastPrivilegesSync < this.CACHE_FRESHNESS_THRESHOLD &&
        this.projectPrivileges) {
        return this.projectPrivileges;
      }

      const projectId = this.getProjectId();
      if (!projectId) {
        // DIAGNOSTIC: returning defaults because authService didn't have a projectId.
        // If this fires for an authenticated user, the bug is upstream in auth, not here.
        console.warn('[Privileges] No projectId available — returning GRANTED_FREE defaults');
        return this.getDefaultPrivileges();
      }

      // Request deduplication - return existing request if in progress
      const requestKey = `getPrivileges-${projectId}`;
      if (this.pendingRequests[requestKey]) {
        return this.pendingRequests[requestKey];
      }

      // Fetching privileges logging silenced

      // Check if this is a page reload. Prefer the modern PerformanceNavigationTiming
      // API (the legacy `performance.navigation.type === 1` is deprecated and
      // misreports on some browsers — Edge/Chrome bfcache restores in particular).
      const isPageReload = (() => {
        if (typeof window === 'undefined' || !window.performance) return false;
        const navEntries = window.performance.getEntriesByType?.('navigation');
        if (navEntries && navEntries.length > 0) {
          return navEntries[0].type === 'reload';
        }
        return window.performance.navigation?.type === 1;
      })();

      // Get the auth token
      const authToken = authService.getAuthState()?.token;

      // Create promise and store for deduplication
      const privilegesPromise = (async () => {
        try {
          // Add timeout to prevent hanging requests.
          // Backend wraps getPrivileges in withHeartbeat (Heroku H12
          // mitigation) — connection stays alive past 30s on slow
          // Snowflake calls, so 8s here would abort before heartbeat
          // could help. 30s gives Snowflake enough room while still
          // bounding worst-case wait for the user.
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

          const response = await fetch(
            `${process.env.GATSBY_STRAPI_URL}/api/company-profile/${projectId}/privileges`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getStrapiToken() || authToken}`,
                ...(forceRefresh ? { 'Cache-Control': 'no-cache, no-store, must-revalidate' } : {})
              },
              signal: controller.signal
            }
          );

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`Failed to get privileges: ${response.status}`);
          }

          const data = await response.json();

          // Cache the privileges for future use
          this.projectPrivileges = data.privileges;
          this.needsRepopulationFlag = data.needsRepopulation || false;
          this.modulesNeedingRepopulation = data.modulesNeedingRepopulation || [];
          this.lastPrivilegesSync = Date.now();
          // Mark the in-memory value as verified against the source for this
          // session — usePermissions stops treating it as suspect.
          this.inMemoryFetchedThisSession = true;

          // Save to cache
          this.saveToCache();

          // Tell hooks/observers a fresh value just landed so React can
          // re-render even if the initial mount used a cached/default value.
          this._notifySubscribers();

          // PREFETCH: If lead firm, prefetch item+s years data in background
          if (data.privileges?.CarbonAccounting === this.CA_PRIVILEGES.ITEMS) {
            const projectId = this.getProjectId();
            if (projectId) {
              // Fire and forget - don't block privileges response
              caItemsCache.prefetch(projectId).catch((e) => {
                if (process.env.NODE_ENV === 'development') {
                  console.warn('[PrivilegesService] item+s prefetch failed:', e);
                }
              });
            }
          }

          return data.privileges;
        } catch (fetchError) {
          // DIAGNOSTIC: surface every failure of the privileges fetch.
          // Without this we silently fall back to GRANTED_FREE defaults, which is
          // exactly the symptom in the TÜV SÜD enterprise-CA-invisible bug.
          console.warn('[Privileges] Fetch failed — returning fallback', {
            projectId,
            errorName: fetchError?.name,
            errorMessage: fetchError?.message,
            isPageReload,
            forceRefresh,
            hadCachedPrivileges: !!this.projectPrivileges,
            cachedCarbonAccounting: this.projectPrivileges?.CarbonAccounting || null
          });

          // For page reloads OR server errors, return default/cached privileges instead of crashing
          if (isPageReload && (fetchError instanceof TypeError || fetchError.name === 'AbortError')) {
            return this.getDefaultPrivileges();
          }

          // Always return cached or default privileges — never crash the app
          return this.projectPrivileges || this.getDefaultPrivileges();
        } finally {
          // Remove from pending requests
          delete this.pendingRequests[requestKey];
        }
      })();

      // Store for deduplication
      this.pendingRequests[requestKey] = privilegesPromise;
      return privilegesPromise;
    } catch (error) {
      return this.getDefaultPrivileges();
    }
  }

      /**
     * Clear repopulation flags in Snowflake after successful repopulation
     * @param {string[]} modules - Optional specific modules to clear
     * @returns {Promise<boolean>} Success status
     */
    async clearRepopulationFlagsInSnowflake(modules = []) {
      try {
        const projectId = this.getProjectId();
        if (!projectId) return false;

        const response = await fetch(
          `${process.env.GATSBY_STRAPI_URL}/api/company-profile/${projectId}/clear-repopulation-flags`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getStrapiToken() || authService.getAuthState()?.token}`,
            },
            body: JSON.stringify({ modules })
          }
        );

        if (!response.ok) {
          console.warn('[PrivilegesService] Failed to clear Snowflake repopulation flags');
          return false;
        }

        return true;
      } catch (error) {
        console.warn('[PrivilegesService] Error clearing Snowflake repopulation flags:', error);
        return false;
      }
    }

  /**
   * Get default privileges as fallback
   * 
   * @returns {Object} Default privileges
   */
  getDefaultPrivileges() {
    return { ...this.defaultPrivileges };
  }

  /**
   * Check if privileges need refresh based on cache age
   * 
   * @returns {boolean} True if refresh is needed
   */
  needsRefresh() {
    return !this.lastPrivilegesSync ||
      (Date.now() - this.lastPrivilegesSync > this.CACHE_FRESHNESS_THRESHOLD);
  }

  /**
   * Check if the current project has CA item+s privilege (lead firm)
   * @returns {boolean} True if CarbonAccounting === 'GRANTED_ITEMS'
   */
  isLeadFirm() {
    return this.projectPrivileges?.CarbonAccounting === this.CA_PRIVILEGES.ITEMS;
  }

  /**
   * Get the CA privilege type for the current project
   * @returns {string} 'GRANTED_FREE' | 'GRANTED_ITEMS'
   */
  getCarbonAccountingPrivilege() {
    return this.projectPrivileges?.CarbonAccounting || this.CA_PRIVILEGES.FREE;
  }

  /**
   * Clear cached privileges (useful for logout)
   */
  clearPrivileges() {
    this.projectPrivileges = null;
    this.needsRepopulationFlag = false;
    this.modulesNeedingRepopulation = [];
    this.lastPrivilegesSync = null;
    this.inMemoryFetchedThisSession = false;

    // Clear item+s cache as well
    caItemsCache.clear();

    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(this.CACHE_KEY);
      }
    } catch (e) {
      // Ignore storage errors
    }

    this._notifySubscribers();
  }

  /**
   * Check if repopulation is needed (set by Snowflake)
   *@returns {boolean} True if repopulation flag is set
  */
  needsRepopulation() {
    return this.needsRepopulationFlag;
  }

  /**
   * Clear the repopulation flag after successful repopulation
   */
  clearRepopulationFlag() {
    this.needsRepopulationFlag = false;
    this.modulesNeedingRepopulation = [];
  }

  /**
   * Map successfully repopulated frontend modules back to the Snowflake module
   * names whose flags may now be cleared. A Snowflake module only qualifies
   * when EVERY frontend module it maps to succeeded — a partial CA load, for
   * example, must leave the CarbonAccounting flag in place for the next login.
   * @param {string[]} succeededFrontendModules - Frontend module names that repopulated successfully
   * @returns {string[]} Snowflake module names safe to clear
   */
  getClearableSnowflakeModules(succeededFrontendModules) {
    const succeeded = new Set(succeededFrontendModules);
    return this.modulesNeedingRepopulation.filter(snowflakeModule => {
      const mapped = this.MODULE_REPOPULATION_MAP[snowflakeModule] || [];
      return mapped.length > 0 && mapped.every(module => succeeded.has(module));
    });
  }

  /**
   * Get the list of frontend modules that need repopulation
   * Maps Snowflake module names to frontend module names
   * @returns {string[]} Array of frontend module names
   */
  getModulesNeedingRepopulation() {
    const frontendModules = [];
    
    for (const snowflakeModule of this.modulesNeedingRepopulation) {
      const mappedModules = this.MODULE_REPOPULATION_MAP[snowflakeModule];
      if (mappedModules) {
        frontendModules.push(...mappedModules);
      } else {
        console.warn(`[PrivilegesService] Unknown module for repopulation: ${snowflakeModule}`);
      }
    }
    
    return frontendModules;
  }
}

// Export singleton instance
export const privilegesService = new PrivilegesService();